import { useEffect, useRef, useState } from 'react';
import { Calendar, EventChangeArg, EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interaction from '@fullcalendar/interaction';

export interface UseVisitCalendarConfig {
  fetchEvents: (
    start: Date,
    end: Date,
    successCallback: (events: any[]) => void,
    failureCallback: (error: Error) => void,
  ) => void;
  onSelect: (info: any) => void;
  onEventClick: (arg: EventClickArg) => void;
  onEventChange: (arg: EventChangeArg) => void;
}

export const useVisitCalendar = (config: UseVisitCalendarConfig) => {
  const calendarRef = useRef<any>(null);
  const calendarInstance = useRef<Calendar | null>(null);
  const [calendarView, setCalendarView] = useState<string>('dayGridMonth');

  useEffect(() => {
    if (!calendarRef.current) return;

    const cal = new Calendar(calendarRef.current, {
      showNonCurrentDates: false,
      plugins: [interaction, dayGridPlugin],
      initialView: 'dayGridMonth',
      views: {
        dayGridWeek: {
          duration: { days: 3 },
        },
      },
      buttonText: {
        today: 'Hoy',
      },
      selectable: true,
      editable: true,
      events: (_info: any, successCallback: any, failureCallback: any) => {
        config.fetchEvents(_info.start, _info.end, successCallback, failureCallback);
      },
      select: config.onSelect,
      eventClick: config.onEventClick,
      eventChange: config.onEventChange,
    });

    cal.render();
    calendarInstance.current = cal;

    return () => {
      cal.destroy();
      calendarInstance.current = null;
    };
  }, []); // intentionally empty — config callbacks are stable closures captured once on mount

  const changeCalendarView = (view: string) => {
    calendarInstance.current?.changeView(view);
    setCalendarView(view);
  };

  const addCalendarEvent = (event: any) => {
    calendarInstance.current?.addEvent(event);
  };

  return {
    calendarRef,
    calendarView,
    changeCalendarView,
    addCalendarEvent,
  };
};
